export type EchoSuppressionResult =
  | "empty"
  | "echo"
  | "divergent"
  | "uncertain";

const ACKNOWLEDGEMENT_ECHOES = new Set([
  "got it",
  "okay",
  "ok",
  "on it",
  "sure",
  "yep",
]);

/** A word character in any script: ASCII `\w`, plus every other letter, mark
 *  and digit, so a transcript in Russian or Japanese is not read as empty. */
const WORD_CHAR = /[\p{L}\p{M}\p{N}_]/u;
const NON_WORD = /[^\p{L}\p{M}\p{N}_\s]/gu;

function normalizeSpeechText(text: string): string {
  return text.toLowerCase().replace(NON_WORD, " ").replace(/\s+/g, " ").trim();
}

function words(text: string): string[] {
  const normalized = normalizeSpeechText(text);
  return normalized ? normalized.split(" ") : [];
}

function overlapRatio(left: string[], right: string[]): number {
  if (!left.length || !right.length) return 0;
  const rightSet = new Set(right);
  const matches = left.filter((word) => rightSet.has(word)).length;
  return matches / left.length;
}

function bestWindowOverlapRatio(left: string[], right: string[]): number {
  if (!left.length || !right.length) return 0;
  const windowSize = Math.min(right.length, Math.max(8, left.length + 4));
  let best = 0;
  for (let index = 0; index <= right.length - windowSize; index += 1) {
    best = Math.max(
      best,
      overlapRatio(left, right.slice(index, index + windowSize)),
    );
    if (best >= 0.75) return best;
  }
  return best;
}

/** Scripts written without spaces between words: a "word" there is a whole
 *  phrase up to the next punctuation, so these match by characters. */
const UNSPACED_CHAR =
  /[\p{Script=Han}\p{Script=Hiragana}\p{Script=Katakana}\p{Script=Hangul}]/gu;
/** Fewer unspaced characters than this ("好", "好的", "はい") are a short
 *  reply that any sentence in that script may contain, never its echo. */
const UNSPACED_ECHO_MIN_CHARS = 3;

export function classifyTtsEcho(
  partialText: string,
  spokenText: string | null | undefined,
): EchoSuppressionResult {
  const partialWords = words(partialText);
  if (!partialWords.length) return "empty";
  const ttsWords = words(spokenText ?? "");
  if (!ttsWords.length) return "divergent";

  const partial = partialWords.join(" ");
  const spoken = ttsWords.join(" ");
  const unspaced = partial.match(UNSPACED_CHAR)?.length ?? 0;
  if (unspaced > 0) {
    if (unspaced < UNSPACED_ECHO_MIN_CHARS) return "uncertain";
    const compact = (text: string) => text.replace(/ /g, "");
    if (compact(spoken).includes(compact(partial))) return "echo";
  } else if (` ${spoken} `.includes(` ${partial} `)) {
    // Whole words only: "no" is not in "know", nor "ok" in "booking".
    return "echo";
  }

  const ratio = bestWindowOverlapRatio(partialWords, ttsWords);
  if (ratio >= 0.75) return "echo";
  if (partialWords.length < 3) return "uncertain";
  return "divergent";
}

export function isLikelyTtsAcknowledgementEcho(text: string): boolean {
  const normalized = normalizeSpeechText(text);
  if (!normalized) return false;
  if (ACKNOWLEDGEMENT_ECHOES.has(normalized)) return true;
  const parts = normalized.split(" ");
  return (
    parts.length <= 4 && parts.every((part) => ACKNOWLEDGEMENT_ECHOES.has(part))
  );
}

/**
 * Remove leading words the microphone picked up from our own speaker.
 * Returns the remainder, "" when the whole transcript is echo, or null when
 * the transcript does not start with spoken words (genuine user speech).
 */
export function stripSpokenPrefix(
  transcript: string,
  spokenTexts: (string | null | undefined)[],
): string | null {
  const transcriptWords = words(transcript);
  if (!transcriptWords.length) return "";
  let longest: string[] = [];
  for (const spoken of spokenTexts) {
    const spokenWords = words(spoken ?? "");
    if (!spokenWords.length) continue;
    const overlap = Math.min(spokenWords.length, transcriptWords.length);
    let match = 0;
    while (match < overlap && transcriptWords[match] === spokenWords[match]) {
      match += 1;
    }
    if (match > longest.length) longest = spokenWords.slice(0, match);
  }
  if (!longest.length) return null;
  if (longest.length >= transcriptWords.length) return "";
  return sliceAfterWords(transcript, longest.length);
}

/**
 * The raw transcript after its first `count` words, keeping the remainder's
 * casing and punctuation for dispatch. Walks raw words with the same
 * tokenization as words() above (word-char runs; apostrophes and punctuation
 * are separators) so the count lines up.
 */
function sliceAfterWords(transcript: string, count: number): string {
  let seen = 0;
  let index = 0;
  while (index < transcript.length && seen < count) {
    if (WORD_CHAR.test(transcript[index] ?? "")) {
      let end = index;
      while (end < transcript.length && WORD_CHAR.test(transcript[end] ?? "")) {
        end += 1;
      }
      seen += 1;
      index = end;
    } else {
      index += 1;
    }
  }
  return transcript.slice(index).replace(/^[\s,.;:!?…"”'’\-–—]+/u, "");
}

/** Fewer leading words than this matching Denker's opening is chance, not
 *  its echo. A run from the middle of what Denker said must match word for
 *  word and be longer: an answer can quote a few of the question's words in
 *  order ("the blue one"). */
const ECHO_RUN_MIN_WORDS = 3;
const ECHO_RUN_MID_MIN_WORDS = 5;
/** Spoken words a misheard echo may skip before the next one matches. */
const ECHO_RUN_SKIP = 1;

/**
 * A quiet echo's word as Whisper may mishear it: one letter added, dropped or
 * changed in a word of three letters or more ("one" heard as "done").
 */
function soundsLikeSpoken(heard: string, spoken: string): boolean {
  if (heard === spoken) return true;
  if (heard.length < 3 || spoken.length < 3) return false;
  if (Math.abs(heard.length - spoken.length) > 1) return false;
  let i = 0;
  let j = 0;
  let edits = 0;
  while (i < heard.length && j < spoken.length) {
    if (heard[i] === spoken[j]) {
      i += 1;
      j += 1;
      continue;
    }
    edits += 1;
    if (edits > 1) return false;
    if (heard.length >= spoken.length) i += 1;
    if (spoken.length >= heard.length) j += 1;
  }
  return edits + (heard.length - i) + (spoken.length - j) <= 1;
}

/**
 * Words of `transcript`, from its start, that follow `spoken` in order from
 * `from` on. `tolerant`: Whisper hears Denker's cancelled voice quietly, so a
 * word may be missed (skipped in `spoken`), slightly misheard (see
 * `soundsLikeSpoken`) or wholly misheard (one transcript word unmatched);
 * otherwise every word must follow the last exactly. Returns the count up
 * to the last matched word.
 */
function inOrderEchoRun(
  transcript: string[],
  spoken: string[],
  from: number,
  tolerant: boolean,
): number {
  const skip = tolerant ? ECHO_RUN_SKIP : 0;
  let at = from;
  let matchedThrough = 0;
  let misses = 0;
  for (let index = 0; index < transcript.length; index += 1) {
    const word = transcript[index] ?? "";
    const ahead = spoken.slice(at, at + skip + 1);
    const exact = ahead.indexOf(word);
    const found =
      exact >= 0 || !tolerant
        ? exact
        : ahead.findIndex((next) => soundsLikeSpoken(word, next));
    if (found >= 0) {
      at += found + 1;
      matchedThrough = index + 1;
      misses = 0;
    } else if (!tolerant || ++misses > 1) {
      break;
    }
  }
  return matchedThrough;
}

/**
 * How many leading words of `transcript` are Denker's own speech, in order:
 * a phrase Whisper transcribed whole can start with Denker's voice before
 * the user's words. Matching in order keeps an answer that reuses the
 * question's words ("the blue one") from being taken for its echo.
 */
export function leadingEchoWordCount(
  transcript: string,
  spokenTexts: (string | null | undefined)[],
): number {
  const transcriptWords = words(transcript);
  const first = transcriptWords[0];
  if (!first) return 0;
  let longest = 0;
  for (const spoken of spokenTexts) {
    const spokenWords = words(spoken ?? "");
    spokenWords.forEach((word, start) => {
      if (word !== first) return;
      const opening = start === 0;
      const run = inOrderEchoRun(transcriptWords, spokenWords, start, opening);
      const needed = opening ? ECHO_RUN_MIN_WORDS : ECHO_RUN_MID_MIN_WORDS;
      if (run >= needed) longest = Math.max(longest, run);
    });
  }
  return longest;
}

/** The transcript without its leading echo of Denker's speech. */
export function stripLeadingEcho(
  transcript: string,
  spokenTexts: (string | null | undefined)[],
): string {
  const count = leadingEchoWordCount(transcript, spokenTexts);
  return count ? sliceAfterWords(transcript, count) : transcript;
}

/** Only an explicit spoken question opens this narrow short-answer exception. */
export function isSpokenQuestion(text: string | null | undefined): boolean {
  return /[?？][\s"'”’)\]]*$/u.test(text?.trim() ?? "");
}

const SHORT_QUESTION_ANSWERS = new Set([
  "okay",
  "ok",
  "right",
  "got it",
  "i see",
  "yes",
  "yeah",
  "yep",
  "sure",
  "no",
  "nope",
]);

export function isShortAnswerToSpokenQuestion(
  text: string,
  spokenText: string | null | undefined,
): boolean {
  return (
    isSpokenQuestion(spokenText) &&
    SHORT_QUESTION_ANSWERS.has(normalizeSpeechText(text)) &&
    classifyTtsEcho(text, spokenText) !== "echo"
  );
}
